import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { createConfirmedTask, deleteConfirmedTask, updateConfirmedTask, isSyncedTask as isProviderTask, TASK_SELECT_FIELDS, type TaskSnapshot } from "@/lib/taskMutations";
import { subscribeTaskChanges } from "@/lib/taskChanges";
import { localEventDayKey } from "@/lib/agendaEventRange";
import { taskDueDay } from "@/lib/taskDates";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";
export const isSyncedTask = isProviderTask;
export { snapshotForTask } from "@/lib/taskMutations";
export type { TaskSnapshot } from "@/lib/taskMutations";

// ── Types ──────────────────────────────────────────────────────────────────

export type Priority = "Critical" | "Important" | "Normal";

export interface Task {
  id: string;
  user_id: string;
  title: string;
  priority: Priority;
  due_date: string | null;
  time_label: string | null;
  done: boolean;
  created_at: string | null;
  // Completion timestamp — set on toggle, anchors the "completed" tab's
  // chronology (migration 20260703220000; legacy rows backfilled = created_at).
  completed_at?: string | null;
  // Provider provenance (e.g. 'google_tasks' — google-tasks-pull upsert).
  // Null/'manual' = created in-app.
  source?: string | null;
  external_id?: string | null;
  client_action_id?: string | null;
}

// tasks has no deleted_at tombstone, so deleting a PROVIDER-synced row is NOT
// durable: google-tasks-pull re-upserts it on the next pull while it still
// exists in Google. Rule 30/06 → provider rows are not deletable in-app.
// ALLOWLIST, not "anything non-manual": locally-created sources ('mirror',
// 'voice', ...) have no external pull that could resurrect them and MUST stay
// deletable (review 03/07 caught 'mirror' tasks being wrongly locked).
export interface FilteredTasks {
  today: Task[];
  upcoming: Task[];
  all: Task[];
  completed: Task[];
}

export interface CreateTaskInput {
  id?: string;
  operation_id?: string;
  source?: "manual" | "mirror" | "voice";
  client_action_id?: string;
  title: string;
  priority: Priority;
  due_date?: string | null;
  time_label?: string | null;
}

export interface UpdateTaskInput {
  title?: string;
  priority?: Priority;
  due_date?: string | null;
  time_label?: string | null;
  done?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function todayDateString(): string {
  return localEventDayKey(new Date().toISOString());
}

function taskDueDateString(task: Task): string | null {
  return taskDueDay(task.due_date);
}

function filterTasks(tasks: Task[]): FilteredTasks {
  const today = todayDateString();

  const todayTasks = tasks.filter((t) => {
    if (t.done) return false;
    const due = taskDueDateString(t);
    return due === null || due === today;
  });

  const upcomingTasks = tasks.filter((t) => {
    if (t.done) return false;
    const due = taskDueDateString(t);
    return due !== null && due > today;
  });

  const allTasks = tasks.filter((t) => !t.done);

  // The completed tab is a HISTORY — most recently completed first (falls
  // back to created_at for legacy rows completed before completed_at existed).
  const completedTasks = tasks
    .filter((t) => t.done)
    .sort((a, b) =>
      new Date(b.completed_at ?? b.created_at ?? 0).getTime() -
      new Date(a.completed_at ?? a.created_at ?? 0).getTime());

  return {
    today: todayTasks,
    upcoming: upcomingTasks,
    all: allTasks,
    completed: completedTasks,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>();
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const ownerRef = useRef<string>();
  const requestRef = useRef(0);
  const loadedRef = useRef(false);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);

  const fetchTasks = useCallback(async (silent = false) => {
    const request = ++requestRef.current;
    const checked = Date.now();
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (request !== requestRef.current) return;
      if (authError || !user) {
        loadedRef.current = false;
        ownerRef.current = undefined;
        setUserId(undefined);
        setCheckedAt(null);
        setTasks([]);
        throw authError ?? new Error("TASK_SESSION_UNAVAILABLE");
      }
      if (ownerRef.current !== user.id) { setTasks([]); loadedRef.current = false; generationRef.current++; }
      ownerRef.current = user.id;
      setUserId(user.id);
      const rows: Task[] = [];
      let cursor: string | undefined;
      for (let page = 0; ; page++) {
        if (page >= 200) throw new Error("TASK_READ_INCOMPLETE");
        let query = supabase.from("tasks").select(TASK_SELECT_FIELDS).eq("user_id", user.id).order("id", { ascending: true }).limit(500);
        if (cursor) query = query.gt("id", cursor);
        const { data, error: fetchError } = await query;
        if (request !== requestRef.current) return;
        if (fetchError) throw fetchError;
        if (!data?.length) break;
        for (const row of data) {
          if (row.user_id !== user.id || typeof row.id !== "string" || (cursor && row.id <= cursor)) throw new Error("TASK_READ_INCOMPLETE");
          cursor = row.id;
          rows.push(row as Task);
        }
      }
      if (request !== requestRef.current) return;
      loadedRef.current = true;
      setTasks(rows.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));
      setCheckedAt(checked);
    } catch (error) {
      if (request === requestRef.current) setError(error instanceof Error ? error.message : "Tasks unavailable");
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const stop = () => { mountedRef.current = false; generationRef.current++; requestRef.current++; };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id;
      if (next === ownerRef.current) return;
      generationRef.current++;
      requestRef.current++;
      ownerRef.current = next;
      loadedRef.current = false;
      setUserId(next);
      setCheckedAt(null);
      setTasks([]);
      setError(null);
      setLoading(!!next);
      if (next) queueMicrotask(() => { if (mountedRef.current && ownerRef.current === next) void fetchTasks(); });
    });
    const unsubscribe = subscribeTaskChanges(change => {
      if (change.userId !== ownerRef.current) return;
      if ("refresh" in change) { void fetchTasks(true); return; }
      if (!loadedRef.current) { void fetchTasks(true); return; }
      // A pre-mutation read must not restore the old row after confirmation.
      requestRef.current += 1;
      setLoading(false);
      setTasks(prev => {
        const others = prev.filter(task => task.id !== change.id);
        return change.task ? [change.task, ...others].sort((a, b) =>
          (b.created_at ?? "").localeCompare(a.created_at ?? "")) : others;
      });
    });
    void fetchTasks();
    return () => { unsubscribe(); subscription.unsubscribe(); stop(); };
  }, [fetchTasks]);

  useAgendaAutoRefresh({ checkedAt, nextEvent: null, loading, error: error !== null,
    enabled: !!userId, refresh: () => fetchTasks(true) });

  const writeGuard = useCallback((uid: string) => {
    const generation = generationRef.current;
    return () => { if (!mountedRef.current || generation !== generationRef.current || uid !== ownerRef.current) throw new Error("TASK_SESSION_UNAVAILABLE"); };
  }, []);
  const createTask = useCallback(async (input: CreateTaskInput) => {
    const uid = ownerRef.current;
    if (!uid) throw new Error("TASK_SESSION_UNAVAILABLE");
    return createConfirmedTask(input, uid, writeGuard(uid));
  }, [writeGuard]);

  const toggleTask = useCallback(async (id: string, snapshot?: TaskSnapshot): Promise<void> => {
    const task = tasks.find(task => task.id === id);
    if (!task) throw new Error("TASK_NOT_LOADED");
    await updateConfirmedTask(id, { done: !task.done }, task.user_id, task.done, snapshot, writeGuard(task.user_id));
  }, [tasks, writeGuard]);

  const deleteTask = useCallback(async (id: string, snapshot?: TaskSnapshot): Promise<void> => {
    const task = tasks.find(task => task.id === id);
    if (!task) throw new Error("TASK_NOT_LOADED");
    if (isSyncedTask(task)) throw new Error("SYNCED_TASK_DELETE_BLOCKED");
    await deleteConfirmedTask(id, task.user_id, snapshot, writeGuard(task.user_id));
  }, [tasks, writeGuard]);

  const updateTask = useCallback(async (id: string, fields: UpdateTaskInput, snapshot?: TaskSnapshot): Promise<void> => {
    const task = tasks.find(task => task.id === id);
    if (!task) throw new Error("TASK_NOT_LOADED");
    await updateConfirmedTask(id, fields, task.user_id, undefined, snapshot, writeGuard(task.user_id));
  }, [tasks, writeGuard]);

  const filtered = filterTasks(tasks);

  return {
    tasks,
    userId,
    filtered,
    loading,
    error,
    fetchTasks,
    createTask,
    toggleTask,
    deleteTask,
    updateTask,
  };
}
